import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./AboutUs.css";
import Navbar from "./Navbar";
import { R2_MEDIA_BASE_URL } from '../../config/mediaConfig';

function AboutUs() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const path = `${R2_MEDIA_BASE_URL}/about_us`;
    const slides = [
        {
            file: "photo-1522202176988-66273c2fd55f",
            tag: t('aboutUs.slides.whoWeAre.tag'),
            title: t('aboutUs.slides.whoWeAre.title'),
            text: t('aboutUs.slides.whoWeAre.text')
        },
        {
            file: "photo-1509062522246-3755977927d7",
            tag: t('aboutUs.slides.ourBeginning.tag'),
            title: t('aboutUs.slides.ourBeginning.title'),
            text: t('aboutUs.slides.ourBeginning.text')
        },
        {
            file: "photo-1491438590914-bc09fcaaf77a",
            tag: t('aboutUs.slides.ourPeople.tag'),
            title: t('aboutUs.slides.ourPeople.title'),
            text: t('aboutUs.slides.ourPeople.text')
        },
        {
            file: "photo-1606761568499-6d2451b23c66",
            tag: t('aboutUs.slides.ourImpact.tag'),
            title: t('aboutUs.slides.ourImpact.title'),
            text: t('aboutUs.slides.ourImpact.text')
        },
        {
            file: "photo-1573497019940-1c28c88b4f3e",
            tag: t('aboutUs.slides.ourFuture.tag'),
            title: t('aboutUs.slides.ourFuture.title'),
            text: t('aboutUs.slides.ourFuture.text')
        },
    ];

    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((s) => (s + 1) % slides.length);
        }, 6000);
        return () => clearInterval(interval);
    }, [slides.length]);

    useEffect(() => {
        const revealElements = document.querySelectorAll('.reveal');

        if (!window.IntersectionObserver) {
            revealElements.forEach((element) => element.classList.add('visible'));
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12 }
        );

        revealElements.forEach((element) => observer.observe(element));

        return () => observer.disconnect();
    }, []);

    const changeSlide = (delta) => {
        setCurrentSlide((s) => {
            const next = (s + delta + slides.length) % slides.length;
            return next;
        });
    };

    const goToSlide = (index) => {
        setCurrentSlide(index % slides.length);
    };

    return (
        <>
            <Navbar />

            <section className="slideshow-section">
                {slides.map((slide, idx) => (
                    <div
                        key={slide.file}
                        className={`slide ${idx === currentSlide ? "active" : ""}`}
                        aria-hidden={idx === currentSlide ? "false" : "true"}
                    >
                        <img
                            src={`${path}/${slide.file}`}
                            alt={slide.tag}
                            className="slide-image"
                        />
                        <div className="slide-caption">
                            <span className="slide-tag">{slide.tag}</span>
                            <h2>{slide.title.split("\n").map((line, i) => (<React.Fragment key={i}>{line}<br /></React.Fragment>))}</h2>
                            <p>{slide.text}</p>
                        </div>
                    </div>
                ))}

                {/* Chevrons rather than solid triangles, matching the chapter slideshow's
                    prev/next controls -- see .slide-arrow in AboutUs.css. */}
                <button
                    type="button"
                    className="slide-arrow prev"
                    onClick={() => changeSlide(-1)}
                    aria-label={t('aboutUs.previous')}
                >
                    ‹
                </button>
                <button
                    type="button"
                    className="slide-arrow next"
                    onClick={() => changeSlide(1)}
                    aria-label={t('aboutUs.next')}
                >
                    ›
                </button>

                <div className="slide-dots" id="dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`dot ${i === currentSlide ? "active" : ""}`}
                            onClick={() => goToSlide(i)}
                            aria-label={t('aboutUs.goToSlide', { num: i + 1 })}
                        />
                    ))}
                </div>
            </section>

            <section style={{ background: "var(--bg-secondary)" }}>
                <div className="about-intro reveal visible">
                    <h2>{t('aboutUs.introTitlePrefix')} <em>{t('aboutUs.introTitleEmphasis')}</em></h2>
                    <p>{t('aboutUs.introP1')}</p>
                    <p>{t('aboutUs.introP2')}</p>
                </div>
            </section>

            <section className="mission-section" id="mission">
                <span className="section-badge">{t('aboutUs.missionBadge')}</span>
                <h2>{t('aboutUs.missionTitle')}</h2>
                <p>{t('aboutUs.missionText')}</p>
                <div className="mission-cards">
                    <div className="mission-card reveal visible">
                        <h3>{t('aboutUs.missionCards.equip.title')}</h3>
                        <p>{t('aboutUs.missionCards.equip.desc')}</p>
                    </div>
                    <div className="mission-card reveal visible" style={{ transitionDelay: "0.1s" }}>
                        <h3>{t('aboutUs.missionCards.bridge.title')}</h3>
                        <p>{t('aboutUs.missionCards.bridge.desc')}</p>
                    </div>
                    <div className="mission-card reveal visible" style={{ transitionDelay: "0.2s" }}>
                        <h3>{t('aboutUs.missionCards.safe.title')}</h3>
                        <p>{t('aboutUs.missionCards.safe.desc')}</p>
                    </div>
                </div>
            </section>

            <div className="values-strip">
                <div className="value-item">
                    <div className="value-num">01</div>
                    <h4>{t('aboutUs.values.empathy.title')}</h4>
                    <p>{t('aboutUs.values.empathy.desc')}</p>
                </div>
                <div className="value-item">
                    <div className="value-num">02</div>
                    <h4>{t('aboutUs.values.evidence.title')}</h4>
                    <p>{t('aboutUs.values.evidence.desc')}</p>
                </div>
                <div className="value-item">
                    <div className="value-num">03</div>
                    <h4>{t('aboutUs.values.cultural.title')}</h4>
                    <p>{t('aboutUs.values.cultural.desc')}</p>
                </div>
                <div className="value-item">
                    <div className="value-num">04</div>
                    <h4>{t('aboutUs.values.tested.title')}</h4>
                    <p>{t('aboutUs.values.tested.desc')}</p>
                </div>
            </div>

            <section className="team-section" id="team">
                <h2>{t('aboutUs.teamTitle')}</h2>
                <p>{t('aboutUs.teamIntro')}</p>
                <div className="team-grid">
                    <div className="team-card reveal visible">
                        <img className="team-photo" src={`${path}/photo-1580489944761-15a19d654956`} alt={t('aboutUs.team.lara.name')} />
                        <div className="team-info">
                            <h4>{t('aboutUs.team.lara.name')}</h4>
                            <span className="team-role">{t('aboutUs.team.lara.role')}</span>
                            <p>{t('aboutUs.team.lara.bio')}</p>
                        </div>
                    </div>
                    <div className="team-card reveal visible" style={{ transitionDelay: "0.1s" }}>
                        <img className="team-photo" src={`${path}/photo-1507003211169-0a1dd7228f2d`} alt={t('aboutUs.team.karim.name')} />
                        <div className="team-info">
                            <h4>{t('aboutUs.team.karim.name')}</h4>
                            <span className="team-role">{t('aboutUs.team.karim.role')}</span>
                            <p>{t('aboutUs.team.karim.bio')}</p>
                        </div>
                    </div>
                    <div className="team-card reveal visible" style={{ transitionDelay: "0.2s" }}>
                        <img className="team-photo" src={`${path}/photo-1573496359142-b8d87734a5a2`} alt={t('aboutUs.team.nour.name')} />
                        <div className="team-info">
                            <h4>{t('aboutUs.team.nour.name')}</h4>
                            <span className="team-role">{t('aboutUs.team.nour.role')}</span>
                            <p>{t('aboutUs.team.nour.bio')}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="partners-section reveal" id="partners">
                <div className="focus-fund-banner">
                    <h3>{t('aboutUs.partnership.titlePrefix')} <em>{t('aboutUs.partnership.titleEmphasis')}</em></h3>
                    <p>{t('aboutUs.partnership.desc')}</p>
                    <a href="https://www.focusfundlebanon.org/" target="_blank" rel="noreferrer" className="btn-orange">{t('aboutUs.partnership.visitButton')}</a>
                </div>

                <span className="section-label">{t('aboutUs.institutionsLabel')}</span>
                <div className="partner-logos">
                    <div className="partner-logo-card">
                        {/* <img className="partner-logo-img" src={`${path}/channels4_profile.jpg`} alt={t('aboutUs.partners.aub.logoAlt')} /> */}
                        <span className="partner-name">{t('aboutUs.partners.aub.name')}</span>
                    </div>

                    <div className="partner-logo-card">
                        {/* <img className="partner-logo-img" src={`${path}/LAU_Centennial_Primary_Logo_RGB_full_colors.png`} alt={t('aboutUs.partners.lau.logoAlt')} /> */}
                        <span className="partner-name">{t('aboutUs.partners.lau.name')}</span>
                    </div>

                    <div className="partner-logo-card">
                        {/* <img className="partner-logo-img" src="" alt={t('aboutUs.partners.ndu.logoAlt')} /> */}
                        <span className="partner-name">{t('aboutUs.partners.ndu.name')}</span>
                    </div>

                    <div className="partner-logo-card">
                        {/* <img className="partner-logo-img" src="" alt={t('aboutUs.partners.usj.logoAlt')} /> */}
                        <span className="partner-name">{t('aboutUs.partners.usj.name')}</span>
                    </div>

                    <div className="partner-logo-card">
                        {/* <img className="partner-logo-img" src="" alt={t('aboutUs.partners.lu.logoAlt')} /> */}
                        <span className="partner-name">{t('aboutUs.partners.lu.name')}</span>
                    </div>
                </div>

                <div className="partner-cta reveal">
                    <h3>{t('aboutUs.institutionCta.title')}</h3>
                    <p>{t('aboutUs.institutionCta.reachOut')} <strong>mindfulcare@gmail.com</strong></p>
                    <a href="mailto:mindfulcare@gmail.com" className="btn-primary" style={{ display: "inline-block", textDecoration: "none", marginTop: 16 }}>{t('aboutUs.institutionCta.contactButton')}</a>
                </div>
            </section>

            <div className="cta-outer">
                <div className="cta-section reveal">
                    <div>
                        <h2>{t('aboutUs.finalCta.titlePrefix')} <em>{t('aboutUs.finalCta.titleEmphasis')}</em></h2>
                        <p>{t('aboutUs.finalCta.desc')}</p>
                    </div>
                    <div className="cta-buttons">
                        <a href="/" className="btn-primary" style={{ padding: "14px 32px", fontSize: "1rem", textDecoration: "none" }}>{t('aboutUs.finalCta.getStartedButton')}</a>
                    </div>
                </div>
            </div>

            <div className="reveal">
                <footer className='footer-main' style={{marginTop: '0'}}>
                    <div className="footer-left">
                        <div className="footer-logo">MindfulCare</div>
                        <p>{t('footer.tagline')}</p>
                    </div>

                    <div className="footer-center">
                        <a onClick={() => navigate('/privacy-policy')} className="footer-link">{t('footer.legalPolicies')}</a>
                        <p>{t('footer.contactPrefix')} <a href="mailto:MindfulCare@gmail.com" style={{color: 'rgba(255,255,255,0.7)', textDecoration: 'none'}}>MindfulCare@gmail.com</a></p>
                    </div>

                    <div className="footer-right">
                        <p>{t('footer.copyright')}</p>
                    </div>
                </footer>
            </div>
        </>
    );
}

export default AboutUs;